import { customerFieldProblems, DomainError, looksLikeCompany, validateCustomer, type CustomerInput } from "@szerviz/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { TaxpayerButton } from "../components/CompanyLookup";
import { AddressFields } from "../components/AddressFields";
import { ErrorText } from "../components/ErrorText";
import { Field, TextInput } from "../components/Field";
import { Help } from "../components/Help";
import { MissingNote, ProblemNote, useRequired } from "../components/Required";
import { useApp, useCan } from "../context";
import type { Customer, Vehicle, WorkOrder } from "../data";
import { Plate } from "../components/Plate";
import { CustomerReminderConsent, reminderContactMissing } from "../components/Reminders";
import { VehicleForm } from "./Vehicles";
import { WorkOrderList } from "./WorkOrders";

const EMPTY: CustomerInput = { name: "" };

export function Customers({ openId }: { openId?: string }) {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Customer[] | null>(null);
  const selected = openId ?? null;
  const setSelected = (id: string | null) => go("customers", id ?? undefined);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      setRows(await store.searchCustomers(workshop.id, query));
    } catch (e) {
      setError(e);
    }
  }, [store, workshop.id, query]);

  useEffect(() => {
    const h = setTimeout(() => void load(), 200);
    return () => clearTimeout(h);
  }, [load]);

  if (selected) {
    return (
      <CustomerDetail
        key={selected}
        id={selected === "new" ? null : selected}
        onClose={() => {
          setSelected(null);
          void load();
        }}
      />
    );
  }

  return (
    <section>
      <div className="page-head">
        <h1>
          {t("customers.title")} <Help id="customers.page" />
        </h1>
        <button type="button" className="primary" onClick={() => setSelected("new")}>
          {t("customers.new")}
        </button>
      </div>
      <input
        className="search"
        type="search"
        placeholder={t("customers.searchPlaceholder")}
        aria-label={t("common.search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ErrorText error={error} />
      {rows === null ? (
        <p>{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="muted">{t("common.noResults")}</p>
      ) : (
        <>
          <p className="muted">{t("common.results", { count: String(rows.length) })}</p>
          <table className="list">
            <thead>
              <tr>
                <th>{t("customer.name")}</th>
                <th>{t("customer.city")}</th>
                <th>{t("customer.mobile")}</th>
                <th>{t("customer.email")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} onClick={() => setSelected(c.id)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setSelected(c.id)}>
                  <td>{c.name}</td>
                  <td>{c.city}</td>
                  <td>{[c.mobile, c.phone].filter(Boolean).join(", ")}</td>
                  <td>{c.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function CustomerDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { store, workshop, go, back } = useApp();
  // deleting a customer is administration (a mechanic records and corrects them at the intake)
  const canDelete = useCan("customerDelete");
  const [form, setForm] = useState<CustomerInput>(EMPTY);
  const [customerId, setCustomerId] = useState<string | null>(id);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [touched, setTouched] = useState(!!id);
  const req = useRequired("customer", form, touched);
  // what was loaded or saved last: only typed values are checked (#109, #111)
  const [original, setOriginal] = useState<CustomerInput | undefined>(undefined);
  const problems = customerFieldProblems(form, original);
  const bad = (field: string) => problems.some((p) => p.field === field);
  // a second number is shown only for customers that have two different ones
  const secondPhone = !!form.phone && !!form.mobile && form.phone !== form.mobile;
  const setMainPhone = (v: string | boolean) => {
    setSaved(false);
    setTouched(true);
    setForm((f) => ({ ...f, mobile: String(v), phone: null }));
  };

  useEffect(() => {
    if (!id) return;
    void store.getCustomer(id).then((c) => {
      if (!c) return;
      setForm(c);
      setOriginal(c);
    });
    void store.vehiclesOfCustomer(id).then(setVehicles);
  }, [id, store]);

  const set = (key: keyof CustomerInput) => (v: string | boolean) => {
    setSaved(false);
    setTouched(true);
    setForm((f) => ({ ...f, [key]: v }));
  };
  // a name ending in Kft., Bt., Zrt. ... ticks "Company" by itself (#155); unticked again only if the
  // program ticked it, never after the user set the box
  const [companyByHand, setCompanyByHand] = useState(false);
  const [companyAuto, setCompanyAuto] = useState(false);
  const setName = (v: string | boolean) => {
    const name = String(v);
    set("name")(name);
    if (companyByHand) return;
    if (looksLikeCompany(name)) {
      if (!form.isCompany) {
        setCompanyAuto(true);
        setForm((f) => ({ ...f, isCompany: true }));
      }
    } else if (companyAuto) {
      setCompanyAuto(false);
      setForm((f) => ({ ...f, isCompany: false }));
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (req.missing.length > 0) throw new DomainError("REQUIRED_MISSING");
      if (problems.length > 0) return;
      const valid = validateCustomer(form);
      const c = await store.saveCustomer(workshop.id, valid, customerId ?? undefined);
      // a new customer gets its own address at once, so a reload shows it (not an empty form);
      // replaced, not added, so Back still returns to where the user came from
      if (!customerId) globalThis.history?.replaceState?.(globalThis.history.state, "", `#customers/${c.id}`);
      setForm(c);
      setOriginal(c);
      setCustomerId(c.id);
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  };

  const remove = async () => {
    if (!customerId || !globalThis.confirm?.(t("common.confirmDelete"))) return;
    try {
      await store.deleteCustomer(customerId);
      onClose();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <section>
      <div className="page-head">
        <button type="button" className="link" onClick={() => back("customers")}>
          ← {t("common.back")}
        </button>
        <h1>{customerId ? form.name : t("customers.new")}</h1>
      </div>
      <form className="form-grid" onSubmit={save} noValidate>
        <TextInput label={t("customer.name")} value={form.name} onChange={setName} wide autoFocus={!customerId} required invalid={req.missingField("name")} />
        <Field label={t("customer.isCompany")} help="customer.isCompany">
          <input
            type="checkbox"
            checked={!!form.isCompany}
            onChange={(e) => {
              setCompanyByHand(true);
              set("isCompany")(e.target.checked);
            }}
            aria-label={t("customer.isCompany")}
          />
        </Field>
        <TextInput label={t("customer.taxNumber")} value={form.taxNumber} onChange={set("taxNumber")} required={req.isReq("taxNumber")} invalid={req.missingField("taxNumber") || bad("taxNumber")} />
        {/* a company (or a typed tax number): its name and seat address from NAV */}
        {(form.isCompany || !!form.taxNumber?.trim()) && (
          <TaxpayerButton
            taxNumber={form.taxNumber}
            onFill={(d) => {
              setSaved(false);
              setTouched(true);
              setForm((f) => ({
                ...f,
                name: d.name,
                isCompany: true,
                taxNumber: d.taxNumber,
                postalCode: d.postalCode ?? f.postalCode,
                city: d.city ?? f.city,
                addressLine: d.addressLine ?? f.addressLine,
              }));
            }}
          />
        )}
        {/* the same address fields as the workshop's own (country, postal code checked, city, street line) */}
        <AddressFields
          idPrefix="customer"
          value={{ country: form.country ?? "HU", postalCode: form.postalCode ?? "", city: form.city ?? "", street: form.addressLine ?? "" }}
          onChange={(a) => {
            setSaved(false);
            setTouched(true);
            setForm((f) => ({ ...f, country: a.country === "HU" ? null : a.country, postalCode: a.postalCode, city: a.city, addressLine: a.street }));
          }}
          labels={{ country: t("address.country"), postalCode: t("customer.postalCode"), city: t("customer.city"), street: t("customer.addressLine") }}
          required={{ city: req.isReq("city"), street: req.isReq("addressLine") }}
          invalid={{ city: req.missingField("city"), street: req.missingField("addressLine"), postalCode: bad("postalCode") }}
        />
        {/* one phone number is enough; a second one (often from the old program) shows only when there is one */}
        <TextInput label={t("customer.mobile")} value={form.mobile ?? (secondPhone ? "" : form.phone)} onChange={secondPhone ? set("mobile") : setMainPhone} type="tel" required={req.isReq("phoneAny")} invalid={(req.missingField("mobile") && req.missingField("phone")) || bad("mobile") || (!secondPhone && bad("phone"))} help="customer.phone" />
        {secondPhone && <TextInput label={t("customer.phone")} value={form.phone} onChange={set("phone")} type="tel" invalid={bad("phone")} />}
        <TextInput label={t("customer.email")} value={form.email} onChange={set("email")} type="email" required={req.isReq("email")} invalid={req.missingField("email") || bad("email")} />
        <CustomerReminderConsent
          form={form}
          consentAt={(form as Customer).reminderConsentAt}
          onChange={(reminderChannels) => {
            setSaved(false);
            setTouched(true);
            setForm((f) => ({ ...f, reminderChannels }));
          }}
        />
        <TextInput label={t("customer.contactPerson")} value={form.contactPerson} onChange={set("contactPerson")} />
        <TextInput label={t("customer.contactPhone")} value={form.contactPhone} onChange={set("contactPhone")} type="tel" invalid={bad("contactPhone")} />
        <Field label={t("customer.notes")} wide>
          <textarea value={form.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} aria-label={t("customer.notes")} rows={3} />
        </Field>
        <div className="actions wide">
          <ErrorText error={error} />
          {saved && <span className="ok">{t("common.saved")}</span>}
          <button type="submit" className="primary" disabled={req.missing.length > 0 || problems.length > 0 || reminderContactMissing(form)}>
            {t("common.save")}
          </button>
          <MissingNote missing={req.missing} />
          <ProblemNote problems={problems} />
          {customerId && canDelete && (
            <button type="button" className="danger" onClick={remove}>
              {t("common.delete")}
            </button>
          )}
        </div>
      </form>

      {customerId && (
        <div className="subsection">
          <div className="page-head">
            <h2>{t("customer.vehicles")}</h2>
            <button type="button" onClick={() => setAddingVehicle(true)}>
              {t("customer.addVehicle")}
            </button>
          </div>
          {addingVehicle && (
            <VehicleForm
              initial={{ plateNumber: "", customerId }}
              onDone={async () => {
                setAddingVehicle(false);
                setVehicles(await store.vehiclesOfCustomer(customerId));
              }}
            />
          )}
          {vehicles.length === 0 && <p className="muted">{t("customer.noVehicles")}</p>}
          <CustomerVehicleOrders customerId={customerId} vehicles={vehicles} />
        </div>
      )}
    </section>
  );
}

/**
 * The customer's vehicles, each with its own work orders under it, so with several cars it is
 * clear which order belongs to which (owner, 2026-11-01). Orders without a vehicle come last.
 */
function CustomerVehicleOrders({ customerId, vehicles }: { customerId: string; vehicles: Vehicle[] }) {
  const { t } = useTranslation();
  const { store, go } = useApp();
  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  useEffect(() => {
    void store.workOrdersOfCustomer(customerId).then(setRows);
  }, [store, customerId]);
  const known = new Set(vehicles.map((v) => v.id));
  const loose = (rows ?? []).filter((o) => !o.vehicleId || !known.has(o.vehicleId));
  return (
    <div className="customer-vehicles">
      {vehicles.map((v) => {
        const own = (rows ?? []).filter((o) => o.vehicleId === v.id);
        return (
          <section key={v.id} className="customer-vehicle" aria-label={v.plateNumber}>
            <a className="chip customer-vehicle-head" href={`#vehicles/${v.id}`} onClick={(e) => { e.preventDefault(); go("vehicles", v.id); }}>
              <Plate plate={v.plateNumber} country={v.plateCountry} size="sm" />
              <span className="chip-text">{[v.make, v.model].filter(Boolean).join(" ")}</span>
              <span className="chip-go" aria-hidden>→</span>
            </a>
            {rows === null ? null : own.length === 0 ? (
              <p className="muted small">{t("workOrders.noneYet")}</p>
            ) : (
              <WorkOrderList rows={own} compact />
            )}
          </section>
        );
      })}
      {rows !== null && loose.length > 0 && (
        <section className="customer-vehicle">
          <h3>{t("customer.ordersNoVehicle")}</h3>
          <WorkOrderList rows={loose} compact />
        </section>
      )}
      {rows === null && <p>{t("common.loading")}</p>}
    </div>
  );
}
